import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import HistoryLog from '../../../HistoryLog';
import HistoryField from '../../../HistoryField';
import {
  AssignmentHistoryResponse,
  AssignmentHistoryDetailsModel,
} from 'api/adminApiClient';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export default function WorksiteLocationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;

  // Billing location
  const hasBillingCountryChanged = !isEqual(
    oldData?.billingLocation?.country || '',
    newData?.billingLocation?.country || '',
  );
  const hasBillingStateChanged = !isEqual(
    oldData?.billingLocation?.state ||
      oldData?.billingLocation?.internationalStateName ||
      '',
    newData?.billingLocation?.state ||
      newData?.billingLocation?.internationalStateName ||
      '',
  );
  const hasBillingCityChanged = !isEqual(
    oldData?.billingLocation?.city ||
      oldData?.billingLocation?.internationalCityName ||
      '',
    newData?.billingLocation?.city ||
      newData?.billingLocation?.internationalCityName ||
      '',
  );
  const hasBillingAddressChanged = !isEqual(
    oldData?.billingLocation?.address || '',
    newData?.billingLocation?.address || '',
  );
  const hasBillingZipChanged = !isEqual(
    oldData?.billingLocation?.zipCode || '',
    newData?.billingLocation?.zipCode || '',
  );
  const hasBillingEmailChanged = !isEqual(
    oldData?.billingEmail || '',
    newData?.billingEmail || '',
  );
  const hasBillingLocationChanged =
    hasBillingStateChanged ||
    hasBillingCityChanged ||
    hasBillingAddressChanged ||
    hasBillingEmailChanged ||
    hasBillingZipChanged ||
    hasBillingCountryChanged;

  //Work location
  const hasWorkCountryChanged = !isEqual(
    oldData?.workLocation?.country || '',
    newData?.workLocation?.country || '',
  );
  const hasWorkStateChanged = !isEqual(
    oldData?.workLocation?.state ||
      oldData?.workLocation?.internationalStateName ||
      '',
    newData?.workLocation?.state ||
      newData?.workLocation?.internationalStateName ||
      '',
  );
  const hasWorkCityChanged = !isEqual(
    oldData?.workLocation?.city ||
      oldData?.workLocation?.internationalCityName ||
      '',
    newData?.workLocation?.city ||
      newData?.workLocation?.internationalCityName ||
      '',
  );
  const hasWorkAddressChanged = !isEqual(
    oldData?.workLocation?.address || '',
    newData?.workLocation?.address || '',
  );
  const hasWorkZipChanged = !isEqual(
    oldData?.workLocation?.zipCode || '',
    newData?.workLocation?.zipCode || '',
  );
  const hasWorkLocationChanged =
    hasWorkStateChanged ||
    hasWorkCityChanged ||
    hasWorkAddressChanged ||
    hasWorkZipChanged ||
    hasWorkCountryChanged;

  const renderWorkLocation = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { workLocation } = assignment;
    const sectionName = 'work-location';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Work location</div>
          </Col>
          {hasWorkCountryChanged && (
            <HistoryField
              title="Country"
              value={workLocation?.country}
              dataId={`${sectionName}-country-field`}
            />
          )}
          {hasWorkStateChanged && (
            <HistoryField
              title="State"
              value={
                workLocation?.state || workLocation?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}
          {hasWorkCityChanged && (
            <HistoryField
              title="City"
              value={workLocation?.city || workLocation?.internationalCityName}
              dataId={`${sectionName}-city-field`}
            />
          )}
          {hasWorkAddressChanged && (
            <HistoryField
              title="Address"
              value={workLocation?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
          {hasWorkZipChanged && (
            <HistoryField
              title="ZIP"
              value={workLocation?.zipCode}
              dataId={`${sectionName}-zip-code-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderBillingLocation = (assignment?: AssignmentHistoryResponse) => {
    if (!assignment) return null;

    const { billingLocation, billingEmail } = assignment;

    const sectionName = 'billing-location';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Billing location</div>
          </Col>
          {hasBillingCountryChanged && (
            <HistoryField
              title="Country"
              value={billingLocation?.country}
              dataId={`${sectionName}-country-field`}
            />
          )}
          {hasBillingStateChanged && (
            <HistoryField
              title="State"
              value={
                billingLocation?.state ||
                billingLocation?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}
          {hasBillingCityChanged && (
            <HistoryField
              title="City"
              value={
                billingLocation?.city || billingLocation?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}
          {hasBillingAddressChanged && (
            <HistoryField
              title="Address"
              value={billingLocation?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
          {hasBillingZipChanged && (
            <HistoryField
              title="ZIP"
              value={billingLocation?.zipCode}
              dataId={`${sectionName}-zip-code-field`}
            />
          )}
          {hasBillingEmailChanged && (
            <HistoryField
              title="Email"
              value={billingEmail}
              dataId={`${sectionName}-email-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderWorksiteLocationInformation = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    return (
      <>
        {hasWorkLocationChanged && renderWorkLocation(assignment)}
        {hasBillingLocationChanged && renderBillingLocation(assignment)}
      </>
    );
  };

  if (!hasWorkLocationChanged && !hasBillingLocationChanged) return null;

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderWorksiteLocationInformation(oldData)}
      newData={renderWorksiteLocationInformation(newData)}
    />
  );
}
